/**
 * Projected coordinate systems the viewer can reproject from and to.
 *
 * Only the systems Czech forestry data actually arrives in are registered.
 */
import proj4 from 'proj4';

/**
 * S-JTSK / Křovák East North. The `+towgs84` is EPSG's "S-JTSK to WGS 84 (1)", the seven-parameter
 * position-vector Helmert (accuracy 1 m) — what PROJ picks without grids. The three-parameter
 * shift (589,76,480; "(3)", 6 m) put Kokořínsko data some 10 m off against WGS84/UTM layers.
 */
export const KROVAK_5514 =
  '+proj=krovak +lat_0=49.5 +lon_0=24.8333333333333 +alpha=30.2881397527778 ' +
  '+k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +towgs84=570.8,85.7,462.8,4.998,1.587,5.261,3.56 +units=m +no_defs';

export const CRS_DEFS: Record<string, string> = {
  'EPSG:4326': '+proj=longlat +datum=WGS84 +no_defs',
  'EPSG:3857': '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs',
  'EPSG:5514': KROVAK_5514,
  'EPSG:32633': '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs',
  'EPSG:32634': '+proj=utm +zone=34 +datum=WGS84 +units=m +no_defs',
};

for (const [code, def] of Object.entries(CRS_DEFS)) proj4.defs(code, def);

const WGS84 = 'EPSG:4326';

function normalize(crs: string): string {
  return crs.trim().toUpperCase();
}

export function isKnownCrs(crs: string | undefined | null): boolean {
  return !!crs && normalize(crs) in CRS_DEFS;
}

// `proj4(from, to, xy)` parses both definitions on every call; a layer has millions of
// vertices, so the converter is built once per system and reused.
const converters = new Map<string, proj4.Converter>();
function converter(crs: string): proj4.Converter {
  const code = normalize(crs);
  let c = converters.get(code);
  if (!c) {
    c = proj4(code, WGS84);
    converters.set(code, c);
  }
  return c;
}

/** A coordinate in `crs` to longitude/latitude. Throws for an unregistered system; check with `isKnownCrs`. */
export function toWgs84(crs: string, xy: [number, number]): [number, number] {
  return converter(crs).forward(xy) as [number, number];
}

/** Longitude/latitude to `crs`. Throws for an unregistered system; check with `isKnownCrs`. */
export function projectTo(crs: string, lngLat: [number, number]): [number, number] {
  return converter(crs).inverse(lngLat) as [number, number];
}
